import React from "react";
import { createTheme } from "@material-ui/core/styles";
import { BrandsEnum } from "../enums";
import * as ToggleButtons from "./common/ToggleButtonCommonBase";
import * as Helpers from "./common/Helpers";
import * as DatePicker from "./common/DateTimePickerCommonBase";
import { PositionProperty, TextTransformProperty } from "csstype";

// create default theme to get breakpoints
const theme = createTheme();
const colorCard = {};

const variables = {
  common: {
    borderRadius: 6,
  },
  colors: {
    primary: {
      main: "#141414",
      dark: "#1A1A1A",
      contrastText: "#F6F8F9",
    },
    secondary: {
      main: "#FFFFFF",
      dark: "#000000",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#FF0012",
    },
    info: {
      main: "#1E369B",
      light: "#DBDFEF",
    },
    text: {
      primary: "#333333",
      secondary: "#141414",
      hint: "#808890",
    },

    background: "#FFFFFF",
    backgroundHeader: "#F5F5F5",
    border: "#5C5C5C",
    hover: "#FFFFFF",
    placeholder: "#333333",
    transparent: "#FFFFFF",
    divider: "#D6D6D6",
    selectedBackground: "#F5F5F5",
    hoverBorder: "#141414",
    hoverButton: "#FFFFFF",
    stickyFooter: "#141414",
  },
};
const svgIcons = {
  circleRemove: {
    d: "path('M7 11v2h10v-2H7zm5-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z')",
  },
};

const defaultPathParams: React.SVGProps<SVGPathElement> = {
  stroke: "currentColor",
  strokeWidth: "1.5",
  fill: "none",
};

function getFontFamily(mainFont: string): string {
  return [mainFont ?? "", "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(",");
}

const props = Helpers.PrepareProperties(variables);

// More properties here: https://material-ui.com/customization/default-theme/
export const mg = {
  themeName: "mg",
  brand: BrandsEnum.MG,
  colorCard: colorCard,
  compositionBoxWidth: 816,
  containerMaxWidth: 412,
  pageMaxWidth: 1208,
  centred: "0 auto",
  border: "1px solid ",
  shape: {
    borderRadius: variables.common.borderRadius,
  },
  palette: {
    primary: variables.colors.primary,
    secondary: variables.colors.secondary,
    error: variables.colors.error,
    text: variables.colors.text,
    backgroundSecondary: {
      default: variables.colors.backgroundHeader,
      paper: variables.colors.selectedBackground,
    },
    background: {
      default: variables.colors.background,
      paper: variables.colors.selectedBackground,
    },
    divider: variables.colors.divider,
    grey: {
      400: variables.colors.border,
    },
    disabledColor: variables.colors.placeholder,
    action: {
      hover: "#ECECEC",
    },
    info: variables.colors.info,
  },
  typography: {
    fontFamily: getFontFamily("Favorit Light"),
    color: variables.colors.text.primary,
    h1: {
      fontFamily: getFontFamily("Favorit Regular"),
      fontSize: "2.5rem",
      lineHeight: "3rem",
      color: variables.colors.primary.main,
      [theme.breakpoints.up("md")]: {
        fontSize: "2.25rem",
        lineHeight: "2.75rem",
      },
      textAlign: "center" as "center",
    },
    h2: {
      fontFamily: getFontFamily("Favorit Regular"),
      fontSize: "2rem",
      lineHeight: "2.5rem",
      color: variables.colors.primary.main,
      textAlign: "center" as "center",
      [theme.breakpoints.up("md")]: {
        fontSize: "1.75rem",
        lineHeight: "2.25rem",
      },
      paddingBottom: theme.spacing(4),
    },
    h3: {
      fontFamily: getFontFamily("Favorit Regular"),
      fontSize: "1.5rem",
      lineHeight: "2rem",
      color: variables.colors.primary.main,
      [theme.breakpoints.up("md")]: {
        fontSize: "1.25rem",
        lineHeight: "1.75rem",
      },
    },
    h4: {
      fontFamily: getFontFamily("Favorit Regular"),
      color: variables.colors.primary.main,
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      [theme.breakpoints.up("md")]: {
        fontSize: "1.125rem",
        lineHeight: "1.625rem",
      },
    },
    h5: {
      fontSize: "1.25rem",
      fontFamily: getFontFamily("Favorit Light"),
      color: variables.colors.text.primary,
    },
    h6: {
      fontFamily: getFontFamily("Favorit Light"),
      color: variables.colors.text.primary,
      fontSize: "1rem",
      lineHeight: "1.75rem",
    },
    // helper text
    caption: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
    button: {
      fontSize: "1rem",
      fontWeight: "normal" as "normal",
      lineHeight: "1.5rem",
      minHeight: 48,
    },
    subtitle2: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
      fontWeight: "normal" as "normal",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.primary.main,
    },
    body2: {
      fontSize: "1.2rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
    },
  },
  radioButton: {
    borderRadius: variables.common.borderRadius,
    border: `1px solid ${variables.colors.border}`,
    hoverBackgroundColor: variables.colors.hover,
    activeBorder: `1px solid ${variables.colors.primary.main}`,
    activeBackgroundColor: "#FFFFFF",
    activeFontWeight: 500,
  },
  addOnCard: {
    component: {
      width: 380,
      display: "inline-block",
      marginBottom: "8px",
    },
    card: {
      border: `1px solid ${variables.colors.border}`,
      padding: "25px 34px",
      descriptionText: {
        color: variables.colors.text.primary,
      },
    },
    cardSelected: {
      backgroundColor: "#FFFFFF",
      border: `2px solid ${variables.colors.primary.main}`,
      padding: "25px 34px",
      addText: {
        color: variables.colors.text.secondary,
        fontWeight: 600,
      },
    },
    header: {
      textColor: variables.colors.text.secondary,
      fontWeight: 600,
    },
    addText: {
      color: variables.colors.text.primary,
      fontWeight: 600,
    },
    price: {
      color: variables.colors.text.primary,
      fontWeight: 600,
    },
  },
  checkoutLinks: {
    color: variables.colors.text.secondary,
  },
  appBackgroundColor: variables.colors.background,
  overrides: {
    // Style sheet name. Look for specific component CSS API: https://material-ui.com/api/button/#css
    MuiTypography: {
      root: {
        textTransform: "none" as TextTransformProperty,
      },
    },
    MuiPaper: {
      root: {
        backgroundColor: variables.colors.primary.main,
      },
    },
    MuiButton: {
      // Name of the rule
      root: {
        // Some CSS
        textTransform: "none" as TextTransformProperty,
      },
      textSecondary: {
        color: variables.colors.text.secondary,
        "&:hover": {
          textDecoration: "underline",
        },
      },
      contained: {
        boxShadow: props.buttons.boxShadow,
        borderRadius: 2,
        "&:hover": {
          boxShadow: props.buttons.boxShadow,
        },
        "&:active": {
          boxShadow: props.buttons.boxShadow,
        },
        "&$focusVisible": {
          boxShadow: props.buttons.boxShadow,
        },
      },
      containedPrimary: {
        backgroundColor: variables.colors.primary.main,
        borderRadius: 24,
        fontWeight: 400,
        color: variables.colors.transparent,
        textDecoration: "none",
        "&:hover": {
          border: "2px solid " + variables.colors.primary.main,
          color: variables.colors.primary.main,
          backgroundColor: variables.colors.transparent,
          "@media (hover: none)": {
            backgroundColor: variables.colors.transparent,
          },
          "& span": {
            textDecoration: "none",
          },
        },
      },
      containedSecondary: {
        borderRadius: 24,
        color: variables.colors.primary.main,
        backgroundColor: variables.colors.transparent,
        "&:hover": {
          color: variables.colors.transparent,
          fontWeight: 600,
          backgroundColor: variables.colors.primary.main,
          "& span": {
            textDecoration: "none",
          },
        },
        "&:active": {
          color: variables.colors.primary.dark,
          fontWeight: 600,
          backgroundColor: variables.colors.transparent,
          "& span": {
            textDecoration: "none",
          },
        },
        // this is just to make link styling same as button if link has button classes
        // needed to override link styling by external CSS by making selector more specific
        "a&&": {
          color: variables.colors.primary.main,
          backgroundColor: variables.colors.transparent,
          border: "2px solid " + variables.colors.primary.main,
          "&:hover": {
            color: variables.colors.transparent,
            backgroundColor: variables.colors.primary.main,
            "& span": {
              textDecoration: "none",
            },
          },
          "&:active": {
            color: variables.colors.transparent,
            backgroundColor: variables.colors.primary.main,
            "& span": {
              textDecoration: "none",
            },
          },
        },
      },
      outlinedSecondary: {
        color: variables.colors.primary.main,
        border: "2px solid " + variables.colors.primary.main,
        "&:hover": {
          color: variables.colors.transparent,
          backgroundColor: variables.colors.primary.main,
        },
        "&:active": {
          color: variables.colors.transparent,
          backgroundColor: variables.colors.primary.main,
        },
      },
      text: {
        "&:hover": {
          color: variables.colors.secondary.dark,
          textDecoration: "underline",
        },
        "&:active": {
          color: variables.colors.text.hint,
        },
        color: variables.colors.text.primary,
      },
      textPrimary: {
        color: variables.colors.primary.main,
        "&:hover": {
          color: variables.colors.primary.main,
          backgroundColor: variables.colors.transparent,
          textDecoration: "underline",
        },
        "&:active": {
          backgroundColor: variables.colors.transparent,
        },
      },
      /* Styles applied to the icon element if supplied and `size="medium"`. */
      iconSizeMedium: {
        "& > *:first-child": {
          fontSize: 20,
        },
      },
    },
    MuiRadio: {
      root: {
        color: variables.colors.primary.main,
      },
      colorSecondary: {
        "&.Mui-checked": {
          color: variables.colors.primary.main,
        },
      },
    },
    MuiIconButton: {
      root: {
        color: variables.colors.primary.main,
        "&:hover": {
          color: variables.colors.primary.dark,
        },
      },
      sizeSmall: {
        padding: 0,
      },
    },
    MuiFormLabel: {
      root: {
        color: variables.colors.primary.main,
        "&$focused": {
          color: variables.colors.border,
        },
        fontWeight: 400,
      },
    },
    MuiInputLabel: {
      formControl: {
        color: variables.colors.text.secondary,
        position: "relative" as PositionProperty,
        transform: "none",
        fontWeight: 400,
        lineHeight: "1.75rem", // safe to use InputLabel + <SvgIcon size='small' />
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 0,
        borderBottom: "2px solid " + variables.colors.border,

        backgroundColor: variables.colors.selectedBackground,
        "&:hover": {
          borderColor: variables.colors.border,
          backgroundColor: variables.colors.selectedBackground,
          "& $notchedOutline": {
            borderColor: variables.colors.border,
          },
          "&$focused $notchedOutline": {
            border: "2px solid " + variables.colors.border,
          },
          "&$error $notchedOutline": {
            borderColor: variables.colors.error.main,
            borderWidth: 1,
          },
        },
        "&$focused $notchedOutline": {
          borderColor: variables.colors.primary.main,
          borderWidth: 0,
        },
        "&$error $notchedOutline": {
          borderColor: variables.colors.error.main,
          borderWidth: 1,
        },
      },
      notchedOutline: {
        borderColor: variables.colors.border,
        borderWidth: 0,
      },
      input: {
        "&::-webkit-input-placeholder": props.inputs.placeholder,
        "&::-moz-placeholder": props.inputs.placeholder, // Firefox 19+
        "&:-ms-input-placeholder": props.inputs.placeholder, // IE 11
        "&::-ms-input-placeholder": props.inputs.placeholder, // Edge
      },
    },
    MuiFormHelperText: {
      root: {
        color: variables.colors.text.hint,
      },
      contained: {
        marginLeft: 0,
      },
    },
    MuiListItem: {
      root: {
        border: "1px solid " + variables.colors.border,
        "&$selected": {
          borderLeftWidth: 0,
        },
      },
      button: {
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.backgroundHeader,
        },
        "&$selected": {
          backgroundColor: variables.colors.background,
          fontWeight: "bold",
          "&:hover": {
            backgroundColor: variables.colors.backgroundHeader,
          },
        },
      },
    },
    MuiMenuItem: {
      ...props.listItems,
    },
    MuiSelect: {
      select: {
        "&:focus": {
          backgroundColor: variables.colors.transparent,
        },
        // Setting icon with IconComponent property, looses all select styling, so we change SVG path directly to one we need
        "&~ svg": {
          "&> path": {
            d: "path('M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z')",
          },
        },
      },
      icon: {
        fill: variables.colors.primary.main,
      },
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiSvgIcon: {
      fontSizeInherit: {
        fontSize: "56px",
      },
    },
    MuiLink: {
      underlineHover: {
        "a&": {
          // to increase specificy
          color: variables.colors.border,
          "&:hover": {
            color: variables.colors.border,
            textDecoration: "underline",
          },
          "&:active": {
            color: variables.colors.border,
          },
        },
      },
    },
    MuiFormControlLabel: {
      label: {
        color: variables.colors.primary.dark,
        fontWeight: 400,
      },
    },
    MuiToggleButtonGroup: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.root,
        borderRadius: 24,
      },
      groupedHorizontal: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.groupedHorizontal,
        ...ToggleButtons.SetMuiToggleButtonGroupBorders({
          ...theme,
          shape: {
            borderRadius: 24,
          },
        }),
      },
    },
    MuiPickersToolbarText: {
      toolbarTxt: {
        color: variables.colors.primary.main,
        backgroundColor: variables.colors.transparent,
      },
      toolbarBtnSelected: {
        color: variables.colors.primary.main,
        backgroundColor: variables.colors.transparent,
      },
    },
    MuiPickersToolbar: {
      toolbar: {
        backgroundColor: variables.colors.transparent,
      },
    },
    MuiToggleButton: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButton.root,
        color: variables.colors.text.primary,
        border: "1px solid " + "#858585",
        fontWeight: 400,
        borderRadius: 24,
        "&$selected": {
          fontFamily: getFontFamily("Favorit Regular"),
          fontWeight: "bold",
          color: variables.colors.primary.main,
          borderColor: variables.colors.primary.main,
          backgroundColor: variables.colors.transparent,
          "&:hover": {
            fontFamily: getFontFamily("Favorit Regular"),
            borderColor: variables.colors.primary.main,
            backgroundColor: variables.colors.transparent,
          },
        },
        "&:hover": {
          fontFamily: getFontFamily("Favorit Regular"),
          borderColor: "#858585",
          color: variables.colors.text.primary,
          backgroundColor: variables.colors.transparent,
        },
      },
    },
    MuiPickersDay: {
      daySelected: {
        "&:hover": {
          color: variables.colors.background,
        },
      },
    },
    MuiPickersBasePicker: {
      container: {
        ...DatePicker.CommonBase.MuiPickersBasePicker.container,
      },
      pickerView: {
        backgroundColor: variables.colors.transparent,
      },
    },
    MuiDialogActions: {
      root: {
        backgroundColor: variables.colors.transparent,
      },
    },
    MuiCard: {
      root: {
        width: 385,
      },
    },
  },
  popup: {
    root: {
      fontFamily: getFontFamily("Favorit Regular"),
      "& p": {
        color: variables.colors.selectedBackground,
      },
    },
    h4: {
      color: variables.colors.selectedBackground,
      fontWeight: 400,
      fontFamily: getFontFamily("Favorit Regular"),
    },
  },
  uspContainerBlockWrapper: {
    backgroundColor: "#141414",
  },
  uspBlock: {
    header: {
      fontWeight: 500,
      color: "#FFFFFF",
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      fontFamily: getFontFamily("Favorit Regular"),
      [theme.breakpoints.up("md")]: {
        fontSize: "1.5rem",
        lineHeight: "2rem",
      },
    },
    text: {
      fontWeight: 400,
      color: "#FFFFFF",
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
  },
  footerBar: {
    backgroundColor: variables.colors.stickyFooter,
  },
  footer: {
    bar: {
      color: variables.colors.transparent,
      "& h5": {
        color: variables.colors.transparent,
      },
      "& h6": {
        color: variables.colors.primary.dark,
      },
      "& svg": {
        color: variables.colors.primary.main,
      },
    },
  },
  priceText: {
    totalPriceColor: variables.colors.primary.main,
    fontWeight: 400,
  },
  icons: {
    dateTimeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M4.69492 22C4.20527 22 3.80038 21.8399 3.48023 21.5198C3.16008 21.1996 3 20.7947 3 20.3051V6.29379C3 5.80414 3.16008 5.39925 3.48023 5.0791C3.80038 4.75895 4.20527 4.59887 4.69492 4.59887H6.9548V2.45198C6.9548 2.32015 6.99717 2.21186 7.08192 2.12712C7.16667 2.04237 7.27495 2 7.40678 2C7.53861 2 7.64689 2.04237 7.73164 2.12712C7.81638 2.21186 7.85876 2.32015 7.85876 2.45198V4.59887H15.6554V2.39548C15.6554 2.28249 15.693 2.18832 15.7684 2.11299C15.8437 2.03766 15.9379 2 16.0508 2C16.1638 2 16.258 2.03766 16.3333 2.11299C16.4087 2.18832 16.4463 2.28249 16.4463 2.39548V4.59887H18.7062C19.1959 4.59887 19.6008 4.75895 19.9209 5.0791C20.2411 5.39925 20.4011 5.80414 20.4011 6.29379V20.3051C20.4011 20.7947 20.2411 21.1996 19.9209 21.5198C19.6008 21.8399 19.1959 22 18.7062 22H4.69492ZM4.69492 21.209H18.7062C18.9322 21.209 19.1394 21.1149 19.3277 20.9266C19.516 20.7382 19.6102 20.5311 19.6102 20.3051V10.8136H3.79096V20.3051C3.79096 20.5311 3.88512 20.7382 4.07345 20.9266C4.26177 21.1149 4.46893 21.209 4.69492 21.209ZM3.79096 10.0226H19.6102V6.29379C19.6102 6.0678 19.516 5.86064 19.3277 5.67232C19.1394 5.48399 18.9322 5.38983 18.7062 5.38983H4.69492C4.46893 5.38983 4.26177 5.48399 4.07345 5.67232C3.88512 5.86064 3.79096 6.0678 3.79096 6.29379V10.0226ZM11.7006 14.6554C11.4934 14.6554 11.3098 14.5753 11.1497 14.4153C10.9896 14.2552 10.9096 14.0716 10.9096 13.8644C10.9096 13.6573 10.9896 13.4736 11.1497 13.3136C11.3098 13.1535 11.4934 13.0734 11.7006 13.0734C11.9077 13.0734 12.0913 13.1535 12.2514 13.3136C12.4115 13.4736 12.4915 13.6573 12.4915 13.8644C12.4915 14.0716 12.4115 14.2552 12.2514 14.4153C12.0913 14.5753 11.9077 14.6554 11.7006 14.6554ZM7.18079 14.6554C6.97363 14.6554 6.79002 14.5753 6.62994 14.4153C6.46987 14.2552 6.38983 14.0716 6.38983 13.8644C6.38983 13.6573 6.46987 13.4736 6.62994 13.3136C6.79002 13.1535 6.97363 13.0734 7.18079 13.0734C7.38795 13.0734 7.57156 13.1535 7.73164 13.3136C7.89171 13.4736 7.97175 13.6573 7.97175 13.8644C7.97175 14.0716 7.89171 14.2552 7.73164 14.4153C7.57156 14.5753 7.38795 14.6554 7.18079 14.6554ZM16.2203 14.6554C16.0132 14.6554 15.8296 14.5753 15.6695 14.4153C15.5094 14.2552 15.4294 14.0716 15.4294 13.8644C15.4294 13.6573 15.5094 13.4736 15.6695 13.3136C15.8296 13.1535 16.0132 13.0734 16.2203 13.0734C16.4275 13.0734 16.6111 13.1535 16.7712 13.3136C16.9313 13.4736 17.0113 13.6573 17.0113 13.8644C17.0113 14.0716 16.9313 14.2552 16.7712 14.4153C16.6111 14.5753 16.4275 14.6554 16.2203 14.6554ZM11.7006 18.9492C11.4934 18.9492 11.3098 18.8691 11.1497 18.709C10.9896 18.549 10.9096 18.3653 10.9096 18.1582C10.9096 17.951 10.9896 17.7674 11.1497 17.6073C11.3098 17.4473 11.4934 17.3672 11.7006 17.3672C11.9077 17.3672 12.0913 17.4473 12.2514 17.6073C12.4115 17.7674 12.4915 17.951 12.4915 18.1582C12.4915 18.3653 12.4115 18.549 12.2514 18.709C12.0913 18.8691 11.9077 18.9492 11.7006 18.9492ZM7.18079 18.9492C6.97363 18.9492 6.79002 18.8691 6.62994 18.709C6.46987 18.549 6.38983 18.3653 6.38983 18.1582C6.38983 17.951 6.46987 17.7674 6.62994 17.6073C6.79002 17.4473 6.97363 17.3672 7.18079 17.3672C7.38795 17.3672 7.57156 17.4473 7.73164 17.6073C7.89171 17.7674 7.97175 17.951 7.97175 18.1582C7.97175 18.3653 7.89171 18.549 7.73164 18.709C7.57156 18.8691 7.38795 18.9492 7.18079 18.9492ZM16.2203 18.9492C16.0132 18.9492 15.8296 18.8691 15.6695 18.709C15.5094 18.549 15.4294 18.3653 15.4294 18.1582C15.4294 17.951 15.5094 17.7674 15.6695 17.6073C15.8296 17.4473 16.0132 17.3672 16.2203 17.3672C16.4275 17.3672 16.6111 17.4473 16.7712 17.6073C16.9313 17.7674 17.0113 17.951 17.0113 18.1582C17.0113 18.3653 16.9313 18.549 16.7712 18.709C16.6111 18.8691 16.4275 18.9492 16.2203 18.9492Z'
          fill='#141414'
        />
      </svg>
    ),
    infoIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M12.001 17C12.1153 17 12.2107 16.9614 12.2874 16.8843C12.364 16.8072 12.4023 16.7117 12.4023 16.5977V11.2529C12.4023 11.1389 12.3636 11.0433 12.2864 10.9662C12.2091 10.8891 12.1133 10.8506 11.999 10.8506C11.8847 10.8506 11.7893 10.8891 11.7126 10.9662C11.636 11.0433 11.5977 11.1389 11.5977 11.2529V16.5977C11.5977 16.7117 11.6364 16.8072 11.7136 16.8843C11.7909 16.9614 11.8867 17 12.001 17ZM12 9.41379C12.1628 9.41379 12.2993 9.35872 12.4095 9.24856C12.5196 9.13841 12.5747 9.00192 12.5747 8.83908C12.5747 8.67625 12.5196 8.53975 12.4095 8.4296C12.2993 8.31944 12.1628 8.26437 12 8.26437C11.8372 8.26437 11.7007 8.31944 11.5905 8.4296C11.4804 8.53975 11.4253 8.67625 11.4253 8.83908C11.4253 9.00192 11.4804 9.13841 11.5905 9.24856C11.7007 9.35872 11.8372 9.41379 12 9.41379ZM12.005 22C10.6224 22 9.32224 21.7376 8.10465 21.2129C6.88705 20.6882 5.8279 19.976 4.92721 19.0765C4.02654 18.1769 3.31351 17.1191 2.7881 15.903C2.2627 14.687 2 13.3876 2 12.005C2 10.6224 2.26237 9.32224 2.7871 8.10466C3.31185 6.88705 4.02399 5.82791 4.92353 4.92724C5.82309 4.02655 6.8809 3.31351 8.09695 2.7881C9.31301 2.2627 10.6124 2 11.995 2C13.3776 2 14.6778 2.26237 15.8953 2.7871C17.1129 3.31185 18.1721 4.02399 19.0728 4.92353C19.9734 5.82309 20.6865 6.8809 21.2119 8.09695C21.7373 9.31301 22 10.6124 22 11.995C22 13.3776 21.7376 14.6778 21.2129 15.8953C20.6882 17.113 19.976 18.1721 19.0765 19.0728C18.1769 19.9735 17.1191 20.6865 15.903 21.2119C14.687 21.7373 13.3876 22 12.005 22ZM12 21.1954C14.567 21.1954 16.7414 20.3046 18.523 18.523C20.3046 16.7414 21.1954 14.567 21.1954 12C21.1954 9.43295 20.3046 7.25862 18.523 5.47701C16.7414 3.6954 14.567 2.8046 12 2.8046C9.43295 2.8046 7.25862 3.6954 5.47701 5.47701C3.6954 7.25862 2.8046 9.43295 2.8046 12C2.8046 14.567 3.6954 16.7414 5.47701 18.523C7.25862 20.3046 9.43295 21.1954 12 21.1954Z'
          fill='#141414'
        />
      </svg>
    ),
    closeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <g opacity='1'>
          <path
            d='M4.65574 20L4 19.3443L11.3443 12L4 4.65574L4.65574 4L12 11.3443L19.3443 4L20 4.65574L12.6557 12L20 19.3443L19.3443 20L12 12.6557L4.65574 20Z'
            fill='#FFFFFF'
          />
        </g>
      </svg>
    ),
    selectIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M12 15.0282L19.0734 7.9548C19.1789 7.84934 19.307 7.79661 19.4576 7.79661C19.6083 7.79661 19.7363 7.84934 19.8418 7.9548C19.9473 8.06026 20 8.18832 20 8.33898C20 8.48964 19.9473 8.6177 19.8418 8.72316L12.9492 15.6158C12.8136 15.7514 12.6629 15.8493 12.4972 15.9096C12.3315 15.9699 12.1657 16 12 16C11.8343 16 11.6686 15.9699 11.5028 15.9096C11.3371 15.8493 11.1864 15.7514 11.0508 15.6158L4.15819 8.72316C4.05273 8.6177 4 8.48964 4 8.33898C4 8.18832 4.05273 8.06026 4.15819 7.9548C4.26365 7.84934 4.39171 7.79661 4.54237 7.79661C4.69303 7.79661 4.82109 7.84934 4.92655 7.9548L12 15.0282Z'
          fill='#141414'
        />
      </svg>
    ),
    crossIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <g opacity='0.4'>
          <path
            d='M4.65574 20L4 19.3443L11.3443 12L4 4.65574L4.65574 4L12 11.3443L19.3443 4L20 4.65574L12.6557 12L20 19.3443L19.3443 20L12 12.6557L4.65574 20Z'
            fill='#141414'
          />
        </g>
      </svg>
    ),
    checkmarkSelectedOptionIcon: (
      <>
        <path fill='#1A1A1A' stroke='#1A1A1A' d='M-1.2071-.5H48.5v49.7071z' transform='matrix(1.24418 0 0 1.2493 3.00373 1.01704)' />
        <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <path
            d='M10.3333 15.1034L7.34483 12.1149C7.2682 12.0383 7.1772 11.9952 7.07184 11.9856C6.96648 11.9761 6.8659 12.0192 6.77011 12.1149C6.67433 12.2107 6.62644 12.3065 6.62644 12.4023C6.62644 12.4981 6.67433 12.5939 6.77011 12.6897L9.72989 15.6494C9.9023 15.8218 10.1034 15.908 10.3333 15.908C10.5632 15.908 10.7644 15.8218 10.9368 15.6494L17.1724 9.41379C17.249 9.33716 17.2921 9.24617 17.3017 9.1408C17.3113 9.03544 17.2682 8.93487 17.1724 8.83908C17.0766 8.7433 16.9808 8.6954 16.8851 8.6954C16.7893 8.6954 16.6935 8.7433 16.5977 8.83908L10.3333 15.1034ZM12.005 22C10.6224 22 9.32224 21.7376 8.10465 21.2129C6.88705 20.6882 5.8279 19.976 4.92721 19.0765C4.02654 18.1769 3.31351 17.1191 2.7881 15.903C2.2627 14.687 2 13.3876 2 12.005C2 10.6224 2.26237 9.32224 2.7871 8.10466C3.31185 6.88705 4.02399 5.82791 4.92353 4.92724C5.82309 4.02655 6.8809 3.31351 8.09695 2.7881C9.31301 2.2627 10.6124 2 11.995 2C13.3776 2 14.6778 2.26237 15.8953 2.7871C17.1129 3.31185 18.1721 4.02399 19.0728 4.92353C19.9734 5.82309 20.6865 6.8809 21.2119 8.09695C21.7373 9.31301 22 10.6124 22 11.995C22 13.3776 21.7376 14.6778 21.2129 15.8953C20.6882 17.113 19.976 18.1721 19.0765 19.0728C18.1769 19.9735 17.1191 20.6865 15.903 21.2119C14.687 21.7373 13.3876 22 12.005 22ZM12 21.1954C14.567 21.1954 16.7414 20.3046 18.523 18.523C20.3046 16.7414 21.1954 14.567 21.1954 12C21.1954 9.43295 20.3046 7.25862 18.523 5.47701C16.7414 3.6954 14.567 2.8046 12 2.8046C9.43295 2.8046 7.25862 3.6954 5.47701 5.47701C3.6954 7.25862 2.8046 9.43295 2.8046 12C2.8046 14.567 3.6954 16.7414 5.47701 18.523C7.25862 20.3046 9.43295 21.1954 12 21.1954Z'
            fill='#F5F5F5'
            transform='translate(12 1) scale(0.5)'
          />
        </svg>
      </>
    ),
    checkmarkIconCSS: encodeURIComponent(`<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.3333 15.1034L7.34483 12.1149C7.2682 12.0383 7.1772 11.9952 7.07184 11.9856C6.96648 11.9761 6.8659 12.0192 6.77011 12.1149C6.67433 12.2107 6.62644 12.3065 6.62644 12.4023C6.62644 12.4981 6.67433 12.5939 6.77011 12.6897L9.72989 15.6494C9.9023 15.8218 10.1034 15.908 10.3333 15.908C10.5632 15.908 10.7644 15.8218 10.9368 15.6494L17.1724 9.41379C17.249 9.33716 17.2921 9.24617 17.3017 9.1408C17.3113 9.03544 17.2682 8.93487 17.1724 8.83908C17.0766 8.7433 16.9808 8.6954 16.8851 8.6954C16.7893 8.6954 16.6935 8.7433 16.5977 8.83908L10.3333 15.1034ZM12.005 22C10.6224 22 9.32224 21.7376 8.10465 21.2129C6.88705 20.6882 5.8279 19.976 4.92721 19.0765C4.02654 18.1769 3.31351 17.1191 2.7881 15.903C2.2627 14.687 2 13.3876 2 12.005C2 10.6224 2.26237 9.32224 2.7871 8.10466C3.31185 6.88705 4.02399 5.82791 4.92353 4.92724C5.82309 4.02655 6.8809 3.31351 8.09695 2.7881C9.31301 2.2627 10.6124 2 11.995 2C13.3776 2 14.6778 2.26237 15.8953 2.7871C17.1129 3.31185 18.1721 4.02399 19.0728 4.92353C19.9734 5.82309 20.6865 6.8809 21.2119 8.09695C21.7373 9.31301 22 10.6124 22 11.995C22 13.3776 21.7376 14.6778 21.2129 15.8953C20.6882 17.113 19.976 18.1721 19.0765 19.0728C18.1769 19.9735 17.1191 20.6865 15.903 21.2119C14.687 21.7373 13.3876 22 12.005 22ZM12 21.1954C14.567 21.1954 16.7414 20.3046 18.523 18.523C20.3046 16.7414 21.1954 14.567 21.1954 12C21.1954 9.43295 20.3046 7.25862 18.523 5.47701C16.7414 3.6954 14.567 2.8046 12 2.8046C9.43295 2.8046 7.25862 3.6954 5.47701 5.47701C3.6954 7.25862 2.8046 9.43295 2.8046 12C2.8046 14.567 3.6954 16.7414 5.47701 18.523C7.25862 20.3046 9.43295 21.1954 12 21.1954Z" fill="#141414"/>
    </svg>
    `),
    checkmarkIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M10.3333 15.1034L7.34483 12.1149C7.2682 12.0383 7.1772 11.9952 7.07184 11.9856C6.96648 11.9761 6.8659 12.0192 6.77011 12.1149C6.67433 12.2107 6.62644 12.3065 6.62644 12.4023C6.62644 12.4981 6.67433 12.5939 6.77011 12.6897L9.72989 15.6494C9.9023 15.8218 10.1034 15.908 10.3333 15.908C10.5632 15.908 10.7644 15.8218 10.9368 15.6494L17.1724 9.41379C17.249 9.33716 17.2921 9.24617 17.3017 9.1408C17.3113 9.03544 17.2682 8.93487 17.1724 8.83908C17.0766 8.7433 16.9808 8.6954 16.8851 8.6954C16.7893 8.6954 16.6935 8.7433 16.5977 8.83908L10.3333 15.1034ZM12.005 22C10.6224 22 9.32224 21.7376 8.10465 21.2129C6.88705 20.6882 5.8279 19.976 4.92721 19.0765C4.02654 18.1769 3.31351 17.1191 2.7881 15.903C2.2627 14.687 2 13.3876 2 12.005C2 10.6224 2.26237 9.32224 2.7871 8.10466C3.31185 6.88705 4.02399 5.82791 4.92353 4.92724C5.82309 4.02655 6.8809 3.31351 8.09695 2.7881C9.31301 2.2627 10.6124 2 11.995 2C13.3776 2 14.6778 2.26237 15.8953 2.7871C17.1129 3.31185 18.1721 4.02399 19.0728 4.92353C19.9734 5.82309 20.6865 6.8809 21.2119 8.09695C21.7373 9.31301 22 10.6124 22 11.995C22 13.3776 21.7376 14.6778 21.2129 15.8953C20.6882 17.113 19.976 18.1721 19.0765 19.0728C18.1769 19.9735 17.1191 20.6865 15.903 21.2119C14.687 21.7373 13.3876 22 12.005 22ZM12 21.1954C14.567 21.1954 16.7414 20.3046 18.523 18.523C20.3046 16.7414 21.1954 14.567 21.1954 12C21.1954 9.43295 20.3046 7.25862 18.523 5.47701C16.7414 3.6954 14.567 2.8046 12 2.8046C9.43295 2.8046 7.25862 3.6954 5.47701 5.47701C3.6954 7.25862 2.8046 9.43295 2.8046 12C2.8046 14.567 3.6954 16.7414 5.47701 18.523C7.25862 20.3046 9.43295 21.1954 12 21.1954Z'
          fill='#141414'
        />
      </svg>
    ),
    helpIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M12.001 17C12.1153 17 12.2107 16.9614 12.2874 16.8843C12.364 16.8072 12.4023 16.7117 12.4023 16.5977V11.2529C12.4023 11.1389 12.3636 11.0433 12.2864 10.9662C12.2091 10.8891 12.1133 10.8506 11.999 10.8506C11.8847 10.8506 11.7893 10.8891 11.7126 10.9662C11.636 11.0433 11.5977 11.1389 11.5977 11.2529V16.5977C11.5977 16.7117 11.6364 16.8072 11.7136 16.8843C11.7909 16.9614 11.8867 17 12.001 17ZM12 9.41379C12.1628 9.41379 12.2993 9.35872 12.4095 9.24856C12.5196 9.13841 12.5747 9.00192 12.5747 8.83908C12.5747 8.67625 12.5196 8.53975 12.4095 8.4296C12.2993 8.31944 12.1628 8.26437 12 8.26437C11.8372 8.26437 11.7007 8.31944 11.5905 8.4296C11.4804 8.53975 11.4253 8.67625 11.4253 8.83908C11.4253 9.00192 11.4804 9.13841 11.5905 9.24856C11.7007 9.35872 11.8372 9.41379 12 9.41379ZM12.005 22C10.6224 22 9.32224 21.7376 8.10465 21.2129C6.88705 20.6882 5.8279 19.976 4.92721 19.0765C4.02654 18.1769 3.31351 17.1191 2.7881 15.903C2.2627 14.687 2 13.3876 2 12.005C2 10.6224 2.26237 9.32224 2.7871 8.10466C3.31185 6.88705 4.02399 5.82791 4.92353 4.92724C5.82309 4.02655 6.8809 3.31351 8.09695 2.7881C9.31301 2.2627 10.6124 2 11.995 2C13.3776 2 14.6778 2.26237 15.8953 2.7871C17.1129 3.31185 18.1721 4.02399 19.0728 4.92353C19.9734 5.82309 20.6865 6.8809 21.2119 8.09695C21.7373 9.31301 22 10.6124 22 11.995C22 13.3776 21.7376 14.6778 21.2129 15.8953C20.6882 17.113 19.976 18.1721 19.0765 19.0728C18.1769 19.9735 17.1191 20.6865 15.903 21.2119C14.687 21.7373 13.3876 22 12.005 22ZM12 21.1954C14.567 21.1954 16.7414 20.3046 18.523 18.523C20.3046 16.7414 21.1954 14.567 21.1954 12C21.1954 9.43295 20.3046 7.25862 18.523 5.47701C16.7414 3.6954 14.567 2.8046 12 2.8046C9.43295 2.8046 7.25862 3.6954 5.47701 5.47701C3.6954 7.25862 2.8046 9.43295 2.8046 12C2.8046 14.567 3.6954 16.7414 5.47701 18.523C7.25862 20.3046 9.43295 21.1954 12 21.1954Z'
          fill='#141414'
        />
      </svg>
    ),
    backIcon: (
      <path
        {...defaultPathParams}
        stroke={variables.colors.primary.main}
        strokeLinecap='square'
        strokeMiterlimit='10'
        d='m 15,6 -6,6 6,6'
      />
    ),
    addIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='M 20,12 H 4 m 8,-8 v 16' />,
    statusIndicatorIcon: <circle cx='6' cy='6' r='5' fill={variables.colors.info.main} stroke='white' strokeWidth='2' />,
  },
  messageBox: {
    backgroundColor: "#E3E9F5",
    borderColor: variables.colors.transparent,
    dividerColor: variables.colors.border,
    textAlign: "left",
  },
  header: {
    backgroundColor: variables.colors.background,
  },
  coverageSelect: {
    root: {
      backgroundColor: variables.colors.background,
      CoverageHeaderName: {
        color: variables.colors.primary.main,
      },
      CoverageItemText: {
        color: variables.colors.primary.main,
      },
      CoverageHeaderPrice: {
        color: variables.colors.primary.main,
      },
    },
    selected: {
      backgroundColor: variables.colors.selectedBackground,
      borderColor: variables.colors.primary.main,
      textColor: variables.colors.text.secondary,
    },
    descriptionCard: {
      backgroundColor: variables.colors.divider,
      borderColor: variables.colors.divider,
      textColor: variables.colors.primary.main,
    },
    recommended: {
      backgroundColor: variables.colors.primary.main,
      borderColor: variables.colors.primary.main,
    },
    singleRecommended: {
      secondLineColor: {
        backgroundColor: variables.colors.transparent,
      },
    },
  },
  CheckoutPage: {
    priceText: {
      color: variables.colors.primary.dark,
    },
    bottomTotalPriceText: {
      color: variables.colors.primary.dark,
    },
  },
  ConfirmationPage: {
    ConfirmationButtonOutlined: {
      color: variables.colors.primary.dark,
    },
  },
  confirmationPageHeader: {
    color: variables.colors.primary.dark,
  },
  skeletonHeaderText: {
    background: theme.palette.secondary.contrastText,
  },
  paymentOption: {
    label: {
      lineHeight: "1.75rem",
      color: "#4D4D4D", 
    },
    selectButton: {
      "& svg": {
        color: variables.colors.primary.dark,
      },
    },
    selectedPayment: {
      border: "2px solid" + variables.colors.primary.main,
    },
    color: variables.colors.text.secondary,
  },
  statusIndicator: {
    borderRadius: 6,
  },
  stickyPriceButton: {
    backgroundColor: variables.colors.primary.main,
    hoverColor: variables.colors.primary.dark,
    borderColor: variables.colors.primary.main,
    textColor: variables.colors.primary.contrastText,
  },
};

export default createTheme(mg);
